import { AfterContentInit, Component, ContentChildren, QueryList } from '@angular/core';
import { IWizItemRenderer, WizIcon } from '@wizeoo/akm-component-lib';
import { IVo } from '@wizeoo/akm-compute-lib';
import { WizDateUtils } from '@wizeoo/akm-core-lib';

/**
 * Vignette de base pour l'affichage en mode vignette du MultiDisplay
 *
 * Présente une entête, un corps et un pied:
 *
 * L'entête se décompose en deux parties:
 * - Une première partie avec une section "title" (permettant d'y positionner un WizLabel par exemple)
 * et une section "actionButtons" (permettant d'y positionner des boutons d'actions). La section "title"
 * se dimensionne en fonction de la taille de la section "actionButtons". Les boutons positionner dans la
 * section "actionButtons" doivent avoir comme référence #titleActionButton pour que le calcul de la taille
 * se fasse correctement.
 * - Une seconde partie avec une section "complementaryTitle" (permettant d'y positionner un WizLabel par exemple).
 * Cette seconde partie n'est pas visible par défaut. Pour la rendre visible il faut activer l'attribut showComplementaryTitle.
 *
 * Le corps se compose exclusivement d'une section "body" (permettant d'y positionner ce que l'on souhaite présenter)
 *
 * Le pied n'est pas modifiable. Il présente la date et l'utilisateur de la dernière modification (si l'information est disponibles)
 * ainsi que les icônes qui présentent l'état déprécié et/ou l'état supprimé si tel est le cas.
 * Le caractère déprécié peut être porté par une autre information (ex: deactivated pour les entités métiers)
 */

@Component({
  selector: 'md-base-tile-renderer',
  templateUrl: './md-base-tile.renderer.html',
})
export class MultiDisplayBaseTileRenderer implements AfterContentInit, IWizItemRenderer<null, IVo> {
  // Identification des boutons d'action dans la barre de titre (pour dimensionner le titre)
  // Pour qu'un bouton d'action dans la barre de titre soit prise en compte dans le calcul de la dimension du titre,
  // il faut qu'il que sa référence soit #titleActionButton
  @ContentChildren('titleActionButton', { descendants: true }) actionButtons: QueryList<WizIcon>;

  // La largeur prise par les boutons d'action dans la barre de titre
  private _titleButtonWidth: number = 0;

  // Le VO manipulé
  private _data: IVo;
  public get data(): IVo {
    return this._data;
  }
  public set data(data: IVo) {
    this._data = data;
    // Par défaut, c'est l'attribut deprecated de l'objet qui permet de gérer le caractère déprécié
    this.deprecated = data !== null && data.source !== undefined && data.source.deprecated;
    this.deleted = data !== null && data.source !== undefined && data.source.deleted;
    this.updated = data !== null && data.source !== undefined && data.source.updateTime !== null;
  }

  // Taille maximale que prennent les boutons d'action dans la barre de titre
  ngAfterContentInit(): void {
    this.actionButtons.forEach((button) => {
      this._titleButtonWidth = this._titleButtonWidth + 30; // button.elementRef.nativeElement.offsetWidth;
    });
  }

  /** Gestion de l'affichage du titre complémentaire */
  public showComplementaryTitle: boolean = false;

  /** Date - User de création ou de dernière modification */
  public get footerText(): string {
    let result: string = ' ';
    if (this.data !== null && this.data.source !== undefined) {
      if (this._updated) {
        if (!!this.data.source.updateTime) {
          result = WizDateUtils.dateToLocalizedString(WizDateUtils.stringToDate(this.data.source?.updateTime.toString().substr(0, 8))) + ' - ' + this.data.source.updateBy;
        }  
      } else {
        if (!!this.data.source.createTime) {
          result = WizDateUtils.dateToLocalizedString(WizDateUtils.stringToDate(this.data.source?.createTime.toString().substr(0, 8))) + ' - ' + this.data.source.createBy;
        }  
      }
    }
    return result;
  }

  /**
   * Détermination de la taille maximale du texte du titre
   */
  get titleWidth(): string {
    let result = '100%';
    if (this._titleButtonWidth > 0) {
      result = `calc(100% - 10px - ${this._titleButtonWidth}px)`;
    }
    return result;
  }

  /** Gestion de l'affichage du pied */
  private _showFooter: boolean = true;
  public get showFooter(): boolean {
    return this._showFooter;
  }
  public set showFooter(showFooter: boolean) {
    this._showFooter = showFooter;
  }

  /**
   * Détermination de l'information caractérisant l'affichage déprécié
   */
  private _deprecated: boolean = false;
  public get deprecated(): boolean {
    return this._deprecated;
  }
  public set deprecated(deprecated: boolean) {
    this._deprecated = deprecated;
  }

  /**
   * Détermination de l'information caractérisant l'affichage supprimé
   */
  private _deleted: boolean = false;
  public get deleted(): boolean {
    return this._deleted;
  }
  public set deleted(deleted: boolean) {
    this._deleted = deleted;
  }

  /**
   * Détermination de l'information caractérisant l'affichage de la date de création ou de modification
   */
  private _updated: boolean = false;
  public get updated(): boolean {
    return this._updated;
  }
  public set updated(updated: boolean) {
    this._updated = updated;
  }

  // Déterrmination de la taille maximale du texte du pied en fonction de la dépréciation et de la suppression
  get footerTextWidth(): string {
    let result: string = '100%';
    if (this.data !== null && this.data.source !== undefined) {
      let iconsWidth = 5; // Pour gérer un espace de 5px entre le dernier icône et la bordure droite de la vignette
      if (this.deprecated) {
        iconsWidth = iconsWidth + 12;
      }
      if (this.data.source.deleted) {
        iconsWidth = iconsWidth + 12;
      }
      result = `calc(100% - ${iconsWidth}px)`;
    }
    return result;
  }
}
